import React, { useState, useEffect } from 'react';
import { API_URL } from '../../utils/apiConfig';
import './AccessLogs.css';

const AccessLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initialFilters = {
    eventType: '',
    startDate: '',
    endDate: '',
    search: ''
  };

  const [filters, setFilters] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1
  });
  

  useEffect(() => {
    fetchAccessLogs();
  }, [filters, page, pageSize]);

  const fetchAccessLogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      queryParams.append('page', page);
      queryParams.append('pageSize', pageSize);
      
      const response = await fetch(`${API_URL}/audit/access-logs?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch access logs');
      }
      
      const data = await response.json();
      setLogs(data.logs || []);
      if (data.pagination) {
        setPagination({
          total: data.pagination.total || 0,
          totalPages: data.pagination.totalPages || 1
        });
      } else {
        setPagination({
          total: data.logs?.length || 0,
          totalPages: 1
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getEventTypeColor = (eventType, status) => {
    if (status === 'failed') return 'error';
    switch (eventType) {
      case 'login': return 'success';
      case 'logout': return 'info';
      case 'failed_login': return 'error';
      case 'api_call': return 'default';
      default: return 'default';
    }
  };

  const formatResponseTime = (ms) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const toLocalInputValue = (date) => {
    const result = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
    return result;
  };

  const applyQuickRange = (hours) => {
    const now = new Date();
    const start = new Date(now.getTime() - hours * 60 * 60 * 1000);
    setPage(1);
    setFilters({
      ...filters,
      startDate: toLocalInputValue(start),
      endDate: toLocalInputValue(now)
    });
  };

  const resetFilters = () => {
    setFilters(initialFilters);
    setPage(1);
    setPageSize(100);
  };

  if (loading) {
    return <div className="access-loading">Loading access logs...</div>;
  }

  if (error) {
    return <div className="access-error">Error: {error}</div>;
  }

  return (
    <div className="access-logs-container">
      <div className="access-filters">
        <select 
          value={filters.eventType} 
          onChange={(e) => {
            setPage(1);
            setFilters({...filters, eventType: e.target.value});
          }}
          className="filter-select"
        >
          <option value="">All Events</option>
          <option value="login">Login</option>
          <option value="logout">Logout</option>
          <option value="failed_login">Failed Login</option>
          <option value="api_call">API Call</option>
          <option value="page_view">Page View</option>
        </select>
        
        <input 
          type="datetime-local" 
          value={filters.startDate}
          onChange={(e) => {
            setPage(1);
            setFilters({...filters, startDate: e.target.value});
          }}
          className="filter-date"
          placeholder="Start Date"
        />
        
        <input 
          type="datetime-local" 
          value={filters.endDate}
          onChange={(e) => {
            setPage(1);
            setFilters({...filters, endDate: e.target.value});
          }}
          className="filter-date"
          placeholder="End Date"
        />
        
        <select 
          value={pageSize} 
          onChange={(e) => {
            setPage(1);
            setPageSize(Number(e.target.value));
          }}
          className="filter-select"
        >
          <option value="50">50 per page</option>
          <option value="100">100 per page</option>
          <option value="250">250 per page</option>
          <option value="500">500 per page</option>
        </select>
        <input
          type="text"
          placeholder="Search user, email, endpoint, IP"
          value={filters.search}
          onChange={(e) => {
            setPage(1);
            setFilters({...filters, search: e.target.value});
          }}
          className="filter-input search-input"
        />

        <button
          type="button"
          className="reset-filters-btn"
          onClick={resetFilters}
        >
          Clear Filters
        </button>
      </div>

      <div className="quick-range-row">
        <span className="quick-range-label">Quick Range:</span>
        <div className="quick-range-buttons">
          <button type="button" onClick={() => applyQuickRange(24)}>24 Hours</button>
          <button type="button" onClick={() => applyQuickRange(24 * 7)}>7 Days</button>
          <button type="button" onClick={() => applyQuickRange(24 * 30)}>30 Days</button>
          <button type="button" onClick={() => {
            setPage(1);
            setFilters({...filters, startDate: '', endDate: ''});
          }}>
            All Time
          </button>
        </div>
      </div>

      <div className="access-logs-table-container">
        <table className="access-logs-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Event</th>
              <th>User</th>
              <th>IP Address</th>
              <th>Endpoint</th>
              <th>Status</th>
              <th>Response Time</th>
              <th className="hide-mobile">Device</th>
            </tr>
          </thead>
          <tbody>
            {logs.map(log => (
              <tr key={log.id} className={log.eventStatus === 'failed' ? 'failed-row' : ''}>
                <td className="time-cell">
                  {(() => {
                    const timestamp = log.timestamp || log.createdAt || log.occurredAt;
                    return timestamp ? new Date(timestamp).toLocaleString() : '-';
                  })()}
                </td>
                <td>
                  <span className={`event-badge ${getEventTypeColor(log.eventType, log.eventStatus)}`}>
                    {log.eventType.replace('_', ' ')}
                  </span>
                </td>
                <td>
                  {log.username || log.email || ((log.firstName && log.lastName) ? `${log.firstName} ${log.lastName}` : '-') }
                </td>
                <td className="ip-cell">{log.ipAddress}</td>
                <td className="endpoint-cell">
                  <span className="method-badge">{log.method}</span>
                  <span className="endpoint-text">{log.endpoint}</span>
                </td>
                <td>
                  <span className={`status-code ${log.responseStatus >= 400 ? 'error' : 'success'}`}>
                    {log.responseStatus}
                  </span>
                </td>
                <td>{formatResponseTime(log.responseTime)}</td>
                <td className="hide-mobile">
                  {log.deviceType || 'unknown'}
                  {log.browserInfo?.browser && (
                    <span className="browser-info"> · {log.browserInfo.browser}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="access-pagination">
        <div className="pagination-summary">
          {pagination.total > 0
            ? `Showing ${Math.min((page - 1) * pageSize + 1, pagination.total)} – ${Math.min(page * pageSize, pagination.total)} of ${pagination.total}`
            : 'No results'}
        </div>
        <div className="pagination-controls">
          <button 
            onClick={() => setPage(prev => Math.max(prev - 1, 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <span>Page {page} of {pagination.totalPages}</span>
          <button 
            onClick={() => setPage(prev => Math.min(prev + 1, pagination.totalPages))}
            disabled={page >= pagination.totalPages}
          >
            Next
          </button>
        </div>
      </div>
      
      {logs.length === 0 && (
        <div className="no-logs">No access logs found for the selected filters.</div>
      )}
    </div>
  );
};

export default AccessLogs;
